import { DOCUMENT } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Inject, Injectable, Injector } from "@angular/core";
import { ParamMap } from "@angular/router";
import { AppServiceHelperService } from "../../../../services/app-service-helper.service";
import { ReportDesignerPrintSettings } from "../../models/report-designer-print-settings";
import { LoadThenPrintService } from "./load-then-print.service";
import { NoPrintService } from "./no-print-service";
import { PagedPrintBatchedService } from "./paged-print-batched.service";
import { PrintService } from "./print.service";

/**
 * Determines the print service that should be used
 */
@Injectable({
  providedIn: 'root'
})
export class PrintServiceResolver {
  /**
   * The default print type (PagedPrintBatchedService -> print page by page and don't wait for the whole document)
   */
  private readonly DEFAULT_PRINT_TYPE = 2;//0 or 1 = LoadThenPrintService, 2 = PagedPrintBatchedService (batch 1 page), 3 = PagedPrintBatchedService

  /**
   * The prit service
   */
  private printService: PrintService = new NoPrintService();//default if not resolved

  /**
   * Constructor
   * @param injector
   * @param http
   * @param appServiceHelper
   * @param document
   */
  constructor(private injector: Injector,
    private http: HttpClient,
    private appServiceHelper: AppServiceHelperService,
    @Inject(DOCUMENT) private document: Document) {
  }

  /**
   * Resolve: Gets the print service that should be used
   * @param search
   * @returns
   */
  resolve(search: ParamMap): Promise<PrintService> {
    const reportId: number = parseInt(search.get('ReportId')) || 0;
    const pdfExportId = parseInt(search.get('PdfExportId')) || 0;
    let printType = parseInt(search.get('printType')) || this.DEFAULT_PRINT_TYPE;
    const batchPagesCount = parseInt(search.get('batchPagesCount')) || 2;

    return new Promise((resolve) => {
      if (reportId <= 0) {
        this.printService = new NoPrintService();
        resolve(this.printService);
      } else {
        this.loadPrintSettings(pdfExportId).then(printSettings => {
          const shouldExportHtmlSnapshot = printSettings.printMethod == 6;//HTML
          if (shouldExportHtmlSnapshot) {
            printType = 1;//LoadThenPrintService
          }

          switch (printType) {
            case 2:
              this.printService = new PagedPrintBatchedService(1, printSettings, this.injector, this.document.defaultView);
              break;
            case 3:
              this.printService = new PagedPrintBatchedService(batchPagesCount, printSettings, this.injector, this.document.defaultView);
              break;
            default:
              this.printService = new LoadThenPrintService(printSettings, this.injector, this.document.defaultView);
              break;
          }

          resolve(this.printService);
        });

        //fire and forget
        this.informPrintStarted(pdfExportId);
      }
    });
  }

  /**
   * Gets the print service
   * @returns
   */
  get(): PrintService {
    return this.printService;
  }

  /**
   * Loads the print settings
   * @param pdfExportId
   * @returns
   */
  private loadPrintSettings(pdfExportId: number): Promise<ReportDesignerPrintSettings> {
    return new Promise((resolve, reject) => {
      const url = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', `reportprint/settings?PdfExportId=${pdfExportId}`)
      this.http.get(url).subscribe({
        next: (printSettings: ReportDesignerPrintSettings) => {
          printSettings.serverSidePrint = true;
          if (printSettings.pausedState) {
            printSettings.pausedState = JSON.parse(printSettings.pausedState as string);/* stringified in DB */
          }

          resolve(printSettings);
        }, error: () => {
          reject(new Error('Failed to load print settings!'));
        }
      });
    });
  }

  /**
   * Inform the API that printing started
   * @param pdfExportId
   */
  private informPrintStarted(pdfExportId: number): void {
    if (pdfExportId) {
      const url = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', `reportprint/printing?PdfExportId=${pdfExportId}`)
      this.http.post(url, {}).subscribe(() => {
        //nothing to do
      });
    }
  }

}
